import React from 'react';
import { sendEventV2 } from '@/utils/analytics';

export type VideoPlayerType = 'youtube' | 'cloudflare' | 'twitch';

export interface GenericVideoPlayerInterface {
    getCurrentTime(): number;
    getDuration(): number;
    isEnded(): boolean;
}

// Props used by child components
export interface VideoPlayerCoreProps {
    videoUrl: string;
    videoId: string;
    className?: string;
    hasControls: boolean;
    shouldAutoplay: boolean;
    playerRef: React.MutableRefObject<GenericVideoPlayerInterface | null>;
    onPlay: () => void;
    onPause: () => void;
    onEnded: () => void;
    onReady: () => void;
}

export function trackVideoEngagement(
    eventType: string,
    videoUrl: string,
    additionalStrings: string[] = [],
    numbers: number[] = [],
) {
    sendEventV2('video_engagement', [eventType, videoUrl, ...additionalStrings], numbers);
}

export function checkMilestones(
    currentTime: number,
    duration: number,
    videoUrl: string,
    milestonesTracked: Set<string>,
    onComplete: () => void,
    onMilestone?: (progress: number) => void,
) {
    if (duration === 0) return;

    const percentage = (currentTime / duration) * 100;
    const milestones = [
        { threshold: 10, label: '10%' },
        { threshold: 20, label: '20%' },
        { threshold: 25, label: '25%' },
        { threshold: 30, label: '30%' },
        { threshold: 40, label: '40%' },
        { threshold: 50, label: '50%' },
        { threshold: 60, label: '60%' },
        { threshold: 70, label: '70%' },
        { threshold: 75, label: '75%' },
        { threshold: 80, label: '80%' },
        { threshold: 90, label: '90%' },
        { threshold: 100, label: '100%' },
    ];

    milestones.forEach((milestone) => {
        if (percentage >= milestone.threshold && !milestonesTracked.has(milestone.label)) {
            milestonesTracked.add(milestone.label);
            trackVideoEngagement('milestone', videoUrl, [milestone.label], [currentTime, duration]);

            if (onMilestone) {
                onMilestone(milestone.threshold);
            }
        }
    });
}

export function detectSeek(currentTime: number, duration: number, videoUrl: string, lastKnownTime: number): number {
    const timeDiff = Math.abs(currentTime - lastKnownTime);

    if (timeDiff > 1 && lastKnownTime > 0) {
        trackVideoEngagement('seek', videoUrl, [], [lastKnownTime, currentTime, duration]);
    }

    return currentTime;
}
